import { create } from 'zustand';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** Keep in lockstep with the pre-paint script in index.html. */
export const THEME_STORAGE_KEY = 'latisent-theme';

const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function readPreference(): ThemePreference {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') return value;
  } catch {
    // Private mode / blocked storage: fall back to light.
  }
  // Light is the default for every sign-in until someone picks another theme.
  return 'light';
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return media?.matches ? 'dark' : 'light';
  return preference;
}

function apply(theme: ResolvedTheme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

interface ThemeState {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const initial = readPreference();

/**
 * Light / Dark / System. One store (not a per-component hook) so every
 * consumer, including the sonner Toaster, agrees on the current theme.
 */
export const useThemeStore = create<ThemeState>((set) => ({
  preference: initial,
  resolved: resolve(initial),
  setPreference: (preference) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      // Persistence is best effort; the current document still changes.
    }
    const resolved = resolve(preference);
    apply(resolved);
    set({ preference, resolved });
  },
}));

// Follow the OS while the preference is "system".
media?.addEventListener('change', () => {
  const { preference } = useThemeStore.getState();
  if (preference !== 'system') return;
  const resolved = resolve('system');
  apply(resolved);
  useThemeStore.setState({ resolved });
});
